import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Trash2, FileText, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { loadAnswers, deleteAnswer, clearAllAnswers } from "@/lib/savedAnswers";
import { exportAntisechePDF } from "@/lib/pdfExport";

export default function Antiseche() {
  const [answers, setAnswers] = useState({});
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setAnswers(loadAnswers());
  }, []);

  const entries = useMemo(() => {
    const list = Object.values(answers).sort((a, b) => b.ts - a.ts);
    if (!search.trim()) return list;
    const s = search.trim().toLowerCase();
    return list.filter(
      (e) =>
        e.questionText.toLowerCase().includes(s) ||
        e.answer.toLowerCase().includes(s)
    );
  }, [answers, search]);

  const onDelete = (id) => {
    const next = deleteAnswer(id);
    setAnswers({ ...next });
    toast.success("Réponse supprimée");
  };

  const onClear = () => {
    clearAllAnswers();
    setAnswers({});
    toast.success("Antisèche vidée");
  };

  const onExport = () => {
    const ok = exportAntisechePDF(answers);
    if (!ok) {
      toast.error("Aucune réponse à exporter");
    } else {
      toast.success("Antisèche PDF générée");
    }
  };

  const total = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 bg-grid">
      <header className="sticky top-0 z-20 bg-zinc-950/85 backdrop-blur border-b border-zinc-800">
        <div className="px-5 md:px-8 py-4 flex items-center gap-4 flex-wrap">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-sky-400 transition-colors uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
          <h1 className="font-display text-lg md:text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-sky-400" /> Antisèche IA
          </h1>
          <span
            data-testid="antiseche-count"
            className="text-[10px] uppercase tracking-[0.25em] text-zinc-500"
          >
            {total} réponse{total > 1 ? "s" : ""} sauvegardée
            {total > 1 ? "s" : ""}
          </span>
          <div className="flex-1" />
          <button
            data-testid="export-pdf-btn"
            onClick={onExport}
            disabled={total === 0}
            className="inline-flex items-center gap-1.5 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold px-4 py-2 text-xs uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" />
            Exporter PDF
          </button>
          {total > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid="clear-all-btn"
                  className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-red-500/50 hover:text-red-400 px-3 py-2 text-xs uppercase tracking-widest text-zinc-300 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Tout vider
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                <AlertDialogHeader>
                  <AlertDialogTitle>Vider l'antisèche ?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    Toutes les réponses IA sauvegardées seront supprimées de
                    ton navigateur. Cette action est irréversible.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    data-testid="clear-cancel"
                    className="bg-zinc-800 hover:bg-zinc-700 border-zinc-700"
                  >
                    Annuler
                  </AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="clear-confirm"
                    onClick={onClear}
                    className="bg-red-500 hover:bg-red-400 text-white"
                  >
                    Vider
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </header>

      <main className="px-5 md:px-8 py-8 max-w-4xl mx-auto space-y-6">
        {total === 0 ? (
          <div
            data-testid="antiseche-empty"
            className="border border-dashed border-zinc-800 bg-zinc-950/40 p-12 text-center"
          >
            <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-display text-xl text-zinc-200">
              Ton antisèche est vide
            </h2>
            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
              Génère une réponse IA depuis une question, elle sera
              automatiquement enregistrée ici. Tu pourras ensuite tout
              exporter en PDF pour réviser.
            </p>
            <Link
              to="/"
              className="inline-block mt-5 text-sm uppercase tracking-widest text-sky-400 hover:text-sky-300 border border-sky-500/40 px-4 py-2 transition-colors"
            >
              Aller aux questions →
            </Link>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="antiseche-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher dans tes réponses sauvegardées…"
                className="pl-9 bg-zinc-900 border-zinc-800 rounded-none h-10 focus-visible:ring-sky-500/40 focus-visible:ring-offset-0 focus-visible:border-sky-500"
              />
            </div>

            <div className="space-y-3">
              {entries.map((e, i) => {
                const isOpen = openId === e.questionId;
                return (
                  <article
                    key={e.questionId}
                    data-testid={`antiseche-item-${e.questionId}`}
                    className="bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 transition-colors"
                  >
                    <header className="flex items-start gap-4 px-5 py-4">
                      <div className="text-xs font-mono text-zinc-600 pt-1 w-10 shrink-0">
                        {String(i + 1).padStart(3, "0")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-sky-400 font-bold mb-1">
                          {e.sectionTitle}{" "}
                          <span className="text-zinc-600">
                            · {e.groupTitle}
                          </span>
                        </div>
                        <h3
                          onClick={() =>
                            setOpenId(isOpen ? null : e.questionId)
                          }
                          className="font-display text-base text-zinc-100 cursor-pointer"
                        >
                          {e.questionText}
                        </h3>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
                          {new Date(e.ts).toLocaleString("fr-FR")} ·{" "}
                          {e.answer.length} caractères
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`delete-answer-${e.questionId}`}
                          onClick={() => onDelete(e.questionId)}
                          className="text-zinc-500 hover:text-red-400 transition-colors"
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </header>
                    {isOpen && (
                      <div className="border-t border-zinc-800 px-5 py-4 fade-up">
                        <pre className="whitespace-pre-wrap font-mono text-sm text-zinc-300 leading-relaxed">
                          {e.answer}
                        </pre>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
